import React from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Alert
} from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import api from '../../../axiosConfig';

const { Option } = Select;
const { TextArea } = Input;

const FIRMA_ILEGIBLE_PACIENTE = 'firma_ilegible_paciente';

// La novedad de firma del paciente devuelve el traslado a validación: solo controles terminados o fallidos,
// sin facturar, no TAI y sin otra firma pendiente (el backend valida lo mismo)
export const admiteNovedadFirma = (control) =>
  !!control &&
  ['terminado', 'fallido'].includes(control.estado_servicio) &&
  control.estado_auditoria !== 'facturado' &&
  !control.es_tai &&
  !control.firma_pendiente_otp;

const CrearNovedadModal = ({ visible, onClose, onCreated, controlServicio }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const tipoNovedad = Form.useWatch('tipo_novedad', form);
  const esFirmaPaciente = tipoNovedad === FIRMA_ILEGIBLE_PACIENTE;
  const estadoValidacion = controlServicio?.estado_servicio === 'fallido' ? 'FallidoXvalidar' : 'FinalizadoXvalidar';

  const enviar = async (values) => {
    setLoading(true);
    try {
      const { data } = await api.post('/novedades-control-servicio', {
        ...values,
        control_servicio_id: controlServicio.id
      });
      message.success(data?.message || 'Novedad creada exitosamente');
      form.resetFields();
      onCreated();
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al crear novedad';
      message.error(errorMsg);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (values) => {
    if (values.tipo_novedad !== FIRMA_ILEGIBLE_PACIENTE) {
      enviar(values);
      return;
    }
    Modal.confirm({
      title: 'Enviar a validación de firma',
      content: `El traslado pasará a ${estadoValidacion}, se retirará la firma del acompañante/paciente de este control y quedará "Pendiente de validación OTP" hasta cerrarlo con OTP desde Coordinación o Centro de Control. ¿Desea continuar?`,
      okText: 'Sí, enviar a validar',
      cancelText: 'No',
      okButtonProps: { danger: true },
      onOk: () => enviar(values),
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <span>Reportar Nueva Novedad</span>
        </Space>
      }
      open={visible}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      width={600}
      confirmLoading={loading}
      okText="Crear Novedad"
      cancelText="Cancelar"
      okButtonProps={{ icon: <CheckCircleOutlined /> }}
      cancelButtonProps={{ icon: <CloseCircleOutlined /> }}
    >
      {controlServicio && (
        <Alert
          message={`Control de Servicio: ${controlServicio.codigo_pcs}`}
          description={`Paciente: ${controlServicio.nombre_paciente} - ${controlServicio.numero_documento_paciente}`}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          tipo_novedad: 'documentacion_incompleta'
        }}
      >
        <Form.Item
          name="tipo_novedad"
          label="Tipo de Novedad"
          rules={[{ required: true, message: 'Seleccione el tipo de novedad' }]}
        >
          <Select size="large">
            <Option value="documentacion_incompleta">
              <Space>
                <span style={{ fontSize: '18px' }}>📄</span>
                <span>Documentación Incompleta</span>
              </Space>
            </Option>
            <Option value="datos_incorrectos">
              <Space>
                <span style={{ fontSize: '18px' }}>❌</span>
                <span>Datos Incorrectos</span>
              </Space>
            </Option>
            <Option value="firma_ilegible">
              <Space>
                <span style={{ fontSize: '18px' }}>✍️</span>
                <span>Firma Ilegible</span>
              </Space>
            </Option>
            {admiteNovedadFirma(controlServicio) && (
              <Option value={FIRMA_ILEGIBLE_PACIENTE}>
                <Space>
                  <span style={{ fontSize: '18px' }}>✍️</span>
                  <span>Firma Ilegible del Paciente (validar con OTP)</span>
                </Space>
              </Option>
            )}
            <Option value="otro">
              <Space>
                <span style={{ fontSize: '18px' }}>⚠️</span>
                <span>Otro</span>
              </Space>
            </Option>
          </Select>
        </Form.Item>

        {esFirmaPaciente && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Esta novedad cambia el traslado y la firma"
            description={`Al guardar, el traslado pasa a ${estadoValidacion}, la firma "FIRMADO POR EL ACOMPAÑANTE/PACIENTE" se retira y el control queda "Pendiente de validación OTP". Se repone al cerrar de nuevo el traslado con OTP desde Coordinación o Centro de Control (no se genera otro control de servicio).`}
          />
        )}

        <Form.Item
          name="descripcion"
          label="Descripción del Problema"
          rules={[
            { required: true, message: 'Ingrese una descripción del problema' },
            { min: 10, message: 'La descripción debe tener al menos 10 caracteres' }
          ]}
          extra="Describa claramente el problema encontrado en este control de servicio"
        >
          <TextArea
            rows={6}
            maxLength={1000}
            showCount
            placeholder="Ejemplo: La firma del paciente no es legible. Se requiere que el paciente firme nuevamente el documento de manera clara."
            style={{ fontSize: '14px' }}
          />
        </Form.Item>

        <Alert
          message="Información"
          description={esFirmaPaciente
            ? "La novedad se creará 'En revisión' y se resolverá automáticamente al validar la firma con OTP."
            : "La novedad se creará con estado 'Pendiente' y será asignada automáticamente a tu usuario. Recibirás notificaciones cuando haya actualizaciones."}
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default CrearNovedadModal;
